import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, Microphone, MicrophoneSlash, X, CaretDown, CaretUp, Lock, CrownSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * AICoHostPanel — streamer-only floating control card for the Bidoria.
 * Mounted on the live stream stage when the viewer IS the streamer (or co-host).
 *
 * Lets the streamer:
 *   • Toggle the Bidoria on / off
 *   • Pick one of 9 personalities
 *   • Tap "Hype now" to manually trigger a line
 *   • Mute Bidoria for 60s
 *   • Add custom hype words
 *
 * Gated by access tier:
 *   • full   → Plus Yearly subscriber, no limits
 *   • trial  → Founder Seller within first 7 days; full UI + countdown badge
 *   • locked → Anyone else; UI replaced with an upgrade-to-Plus card
 */
export default function AICoHostPanel({ streamId, lastLine }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, personality: "auctioneer", custom_hype: "" });
  const [personalities, setPersonalities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [muted, setMuted] = useState(false);
  const [hypeOpen, setHypeOpen] = useState(false);
  const [hypeDraft, setHypeDraft] = useState("");
  const [hypeDirty, setHypeDirty] = useState(false);
  const [access, setAccess] = useState(null);

  const isLocked = access?.tier === "locked";
  const isTrial = access?.tier === "trial";
  const isFull = access?.tier === "full";

  // Listen for cross-component requests to open the panel — fired by the
  // mobile rail's "Bidoria" button, since on mobile the floating FAB itself
  // is hidden to keep the video frame clean.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("bidoria:open", handler);
    return () => window.removeEventListener("bidoria:open", handler);
  }, []);

  // Load static personality list + current settings + access tier on mount.
  // We deliberately fire the three calls independently rather than via
  // Promise.all so that a single 401 (e.g. anonymous session, expired
  // cookie) doesn't blank the whole panel — each request fills in its own
  // slice of state and the rest still hydrates.
  useEffect(() => {
    let dead = false;
    (async () => {
      api.get(`/ai-cohost/personalities`)
        .then((r) => { if (!dead) setPersonalities(r.data?.items || []); })
        .catch(() => { /* non-fatal */ });
      api.get(`/streams/${streamId}/ai-cohost/settings`)
        .then((r) => {
          if (dead) return;
          const initial = r.data || { enabled: false, personality: "auctioneer", custom_hype: "" };
          setSettings(initial);
          setHypeDraft(initial.custom_hype || "");
        })
        .catch(() => { /* non-fatal */ });
      api.get(`/ai-cohost/access`)
        .then((r) => { if (!dead) setAccess(r.data || null); })
        .catch(() => { /* non-fatal */ });
    })();
    return () => { dead = true; };
  }, [streamId]);

  const persist = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put(
        `/streams/${streamId}/ai-cohost/settings`,
        patch,
      );
      setSettings(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend returns {message, needs_upgrade, upgrade_plan, upgrade_url}
      // when the user's tier is locked. Re-fetch access so the UI flips to
      // the upgrade card instead of toasting "[object Object]".
      if (detail && typeof detail === "object" && detail.needs_upgrade) {
        toast.error(detail.message || "Upgrade to Plus Yearly to unlock.");
        try {
          const ax = await api.get(`/ai-cohost/access`);
          setAccess(ax.data || null);
        } catch { /* noop */ }
      } else if (e?.response?.status === 401) {
        // Session expired or cookie not riding along. Tell the user to log
        // back in rather than the cryptic "Not authenticated".
        toast.error("Your session expired. Please log in again to use Bidoria.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't save settings");
      }
    } finally {
      setBusy(false);
    }
  };

  // One-tap Stripe checkout for Plus Yearly. Replaces the old static
  // <Link to="/plus"> route — drops the seller straight onto the Stripe
  // hosted checkout page so they can subscribe without losing context.
  const [checkingOut, setCheckingOut] = useState(false);
  const startPlusCheckout = async () => {
    if (checkingOut) return;
    setCheckingOut(true);
    try {
      const r = await api.post(
        `/plus/checkout`,
        { plan: "yearly", origin_url: window.location.origin },
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't start checkout. Try again in a moment.");
        setCheckingOut(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't start checkout. Try again.");
      setCheckingOut(false);
    }
  };

  // Browser autoplay policy gates audio.play() until a user gesture has
  // unlocked the page. We unlock here, INSIDE the click handler, so that
  // when the AICoHostPlayer later receives WS audio chunks from Bidoria
  // its `<audio>.play()` calls succeed silently. Without this, the very
  // first Bidoria broadcast on iOS/Safari/Chrome-mobile would be dropped
  // by the autoplay block — making it look like Bidoria "doesn't work"
  // even though the backend fired a perfectly valid hype.
  const unlockAudioPlayback = () => {
    try {
      // 0.05s of silence — universally decodable across browsers. Plays
      // synchronously inside the gesture chain so it counts as
      // user-initiated, which whitelists future programmatic plays.
      const SILENT_MP3 =
        "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAA";
      const a = new Audio(SILENT_MP3);
      a.volume = 0;
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* noop */ });
      // Notify any mounted AICoHostPlayer to also prime its <audio> ref.
      window.dispatchEvent(new CustomEvent("bidoria:unlock-audio"));
    } catch { /* noop */ }
  };

  const onToggle = () => {
    // Unlock BEFORE the persist call — same synchronous tick as the
    // user's tap → counts as the gesture.
    if (!settings.enabled) unlockAudioPlayback();
    persist({ enabled: !settings.enabled });
  };
  const onPickPersonality = (id) => persist({ personality: id });

  const saveHype = async () => {
    if (!hypeDirty) return;
    await persist({ custom_hype: hypeDraft });
    setHypeDirty(false);
  };

  const onHypeNow = async () => {
    if (!settings.enabled) {
      toast.info("Turn the Bidoria on first.");
      return;
    }
    // Same unlock — covers the case where the seller toggled Bidoria
    // ON before mounting (state restored from backend) and "Hype now"
    // is their first audio-relevant interaction.
    unlockAudioPlayback();
    setBusy(true);
    try {
      await api.post(`/streams/${streamId}/ai-cohost/hype`, {});
      toast.success("Hyping the room…");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        toast.error("Your session expired. Please log in again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Hype failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onMute60 = async () => {
    setBusy(true);
    try {
      await api.post(
        `/streams/${streamId}/ai-cohost/mute`,
        { seconds: 60 },
      );
      setMuted(true);
      toast.success("Muted for 60 seconds");
      setTimeout(() => setMuted(false), 60_000);
    } catch (e) {
      toast.error("Couldn't mute");
    } finally {
      setBusy(false);
    }
  };

  const currentPersona = personalities.find((p) => p.id === settings.personality);

  if (!open) {
    // The Bidoria floating button has been retired on BOTH mobile and
    // desktop. The single source of truth for opening this panel is now
    // the dispatch of the `bidoria:open` window event:
    //   • Mobile → the "Bidoria" tile inside the More sheet (seller +
    //     Plus access only)
    //   • Desktop → the right-rail Bidoria circle (also gated server-side
    //     by the seller's Plus tier — viewers in non-Plus streams never
    //     see it)
    // No visible button is rendered here.
    return null;
  }

  return (
    <div
      className="block absolute z-40 inset-x-3 bottom-20 sm:inset-x-auto md:bottom-40 md:right-3 md:w-[280px] md:max-w-[calc(100vw-24px)] rounded-2xl bg-zinc-900/95 backdrop-blur-md text-white border border-white/10 shadow-2xl overflow-hidden"
      data-testid="ai-cohost-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-1.5">
          <Sparkle size={14} weight="fill" className="text-amber-400" />
          <span className="text-xs font-semibold tracking-wide uppercase">
            Bidoria<sup className="text-[8px] ml-0.5">™</sup>
            <span className="text-white/45 font-normal normal-case ml-1">
              by Bidorah
            </span>
          </span>
          {isTrial && (
            <span
              className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase"
              data-testid="ai-cohost-trial-badge"
              title={access?.reason}
            >
              Trial · {access?.trial_days_remaining}d
            </span>
          )}
          {isFull && (
            <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 px-1.5 py-0.5 text-[9px] font-bold uppercase">
              <CrownSimple size={9} weight="fill" />
              Plus
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-white/60 hover:text-white"
          data-testid="ai-cohost-close-btn"
        >
          <X size={14} />
        </button>
      </div>

      {/* IP / proprietary notice — protects the Bidoria mark + concept. */}
      <div
        className="px-3 py-1.5 bg-black/40 border-b border-white/5 text-[9px] uppercase tracking-wider text-white/45 flex items-center justify-between gap-2"
        data-testid="ai-cohost-ip-notice"
      >
        <span>
          Bidoria<sup className="text-[7px]">™</sup>{" "}
          <span className="normal-case tracking-normal">
            an original feature by Bidorah
          </span>
        </span>
        <span className="normal-case tracking-normal text-white/35">
          © {new Date().getFullYear()} Bidorah, Inc.
        </span>
      </div>

      {isLocked ? (
        <div className="px-4 py-5 text-center space-y-3" data-testid="ai-cohost-locked">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/30">
            <Lock size={18} className="text-amber-300" weight="fill" />
          </div>
          <div className="text-sm font-bold leading-snug">
            {access?.reason || "Upgrade to Plus Yearly to unlock the Bidoria."}
          </div>
          <div className="text-[11px] text-white/55 leading-snug">
            $299/yr · cancel anytime · all 9 personalities, custom hype words, unlimited streams.
          </div>
          <button
            type="button"
            onClick={startPlusCheckout}
            disabled={checkingOut}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-amber-400/60 disabled:cursor-wait text-black font-bold text-xs px-4 py-2 transition-colors w-full justify-center"
            data-testid="ai-cohost-upgrade-cta"
          >
            <CrownSimple size={12} weight="fill" />
            {checkingOut ? "Opening checkout…" : "Unlock Bidoria · $299/yr"}
          </button>
          <Link
            to="/plus"
            className="block text-[10px] text-white/45 hover:text-white/70 underline"
            onClick={() => setOpen(false)}
            data-testid="ai-cohost-upgrade-learn-more"
          >
            See everything Plus includes →
          </Link>
        </div>
      ) : (
      <div className="px-3 py-3 space-y-3">
        {/* Enabled toggle */}
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
            settings.enabled
              ? "bg-emerald-500/20 border border-emerald-400/30 text-emerald-200"
              : "bg-white/5 border border-white/10 text-white/80 hover:bg-white/10"
          }`}
          data-testid="ai-cohost-toggle-btn"
        >
          <span>{settings.enabled ? "Co-Host is ON" : "Turn ON"}</span>
          <span className={`w-9 h-5 rounded-full p-0.5 transition-colors ${settings.enabled ? "bg-emerald-400" : "bg-white/20"}`}>
            <span
              className={`block w-4 h-4 rounded-full bg-white transition-transform ${settings.enabled ? "translate-x-4" : ""}`}
            />
          </span>
        </button>

        {/* Personality picker */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Personality</div>
          <div className="grid grid-cols-1 gap-1">
            {personalities.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPickPersonality(p.id)}
                disabled={busy}
                className={`text-left rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                  settings.personality === p.id
                    ? "bg-amber-500/20 border border-amber-400/40 text-amber-100"
                    : "bg-white/5 border border-transparent text-white/80 hover:bg-white/10"
                }`}
                data-testid={`ai-cohost-persona-${p.id}`}
              >
                <div className="font-semibold">{p.label}</div>
                <div className="text-[10px] text-white/55 leading-snug">{p.blurb}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Action row */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onHypeNow}
            disabled={busy || !settings.enabled}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wide px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="ai-cohost-hype-btn"
          >
            <Sparkle size={12} weight="fill" />
            Hype now
          </button>
          <button
            type="button"
            onClick={onMute60}
            disabled={busy}
            className="flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 text-white/80 px-3 py-2 disabled:opacity-40 transition-colors"
            data-testid="ai-cohost-mute-btn"
            title="Mute Bidoria for 60 seconds"
          >
            {muted ? <MicrophoneSlash size={14} /> : <Microphone size={14} />}
          </button>
        </div>

        {/* Custom hype words — collapsible */}
        <div className="rounded-lg bg-white/5 border border-white/10 overflow-hidden">
          <button
            type="button"
            onClick={() => setHypeOpen((v) => !v)}
            className="w-full flex items-center justify-between px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/70 hover:text-white"
            data-testid="ai-cohost-hype-toggle"
          >
            <span className="flex items-center gap-1.5">
              Your hype words
              {settings.custom_hype ? (
                <span className="rounded-full bg-amber-400/20 text-amber-200 px-1.5 py-0.5 text-[9px] normal-case tracking-normal">
                  on
                </span>
              ) : null}
            </span>
            {hypeOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
          {hypeOpen && (
            <div className="px-2.5 pb-2.5 space-y-1.5">
              <textarea
                rows={3}
                maxLength={240}
                placeholder={`e.g. say "LET'S GO" on big bids, shoutout @vinny when he bids, we sell sneakers and Pokemon out of NYC`}
                value={hypeDraft}
                onChange={(e) => {
                  setHypeDraft(e.target.value);
                  setHypeDirty(true);
                }}
                onBlur={saveHype}
                className="w-full text-xs rounded-md bg-black/40 border border-white/10 text-white p-2 placeholder:text-white/30 focus:outline-none focus:border-amber-400/40 resize-none"
                data-testid="ai-cohost-hype-input"
              />
              <div className="flex items-center justify-between text-[10px] text-white/40">
                <span>Bidoria weaves these into its lines.</span>
                <span>{hypeDraft.length}/240</span>
              </div>
            </div>
          )}
        </div>

        {/* Most recent line */}
        {lastLine?.text ? (
          <div className="rounded-lg bg-black/40 border border-white/10 px-2.5 py-2 text-[11px] text-white/85 leading-snug">
            <span className="text-amber-300/90 font-semibold mr-1">
              {currentPersona?.label || "Bidoria"}:
            </span>
            "{lastLine.text}"
          </div>
        ) : (
          <div className="text-[10px] text-white/40 italic">
            Lines will appear here once Bidoria starts riffing.
          </div>
        )}

        {/* Trial -> upgrade nudge while inside the trial window */}
        {isTrial && (
          <button
            type="button"
            onClick={startPlusCheckout}
            disabled={checkingOut}
            className="block w-full text-center text-[10px] text-amber-300/90 hover:text-amber-200 disabled:opacity-50 underline"
            data-testid="ai-cohost-trial-upgrade"
          >
            {checkingOut
              ? "Opening checkout…"
              : `Trial ends in ${access?.trial_days_remaining}d — keep it forever for $299/yr`}
          </button>
        )}
      </div>
      )}

      {/* Patent-pending footer — a defensive IP signal that sits at the very
          bottom of the Bidoria control panel so every streamer sees it. The
          ™ + "Patent Pending" wording deters cloning and asserts originality.
          Toggle the underlying flag in `process.env.REACT_APP_BIDORIA_PATENT_STATUS`
          if/when the actual USPTO status changes. */}
      <div
        className="px-3 py-2 bg-black/50 border-t border-white/10 text-center text-[9px] uppercase tracking-[0.18em] text-white/45 flex items-center justify-center gap-1.5"
        data-testid="ai-cohost-patent-footer"
        title="An original Bidorah feature. U.S. provisional patent application filed."
      >
        <Sparkle size={9} weight="fill" className="text-amber-400/70" />
        <span>
          Bidoria<sup className="text-[7px]">™</sup>
          <span className="text-white/30 mx-1">·</span>
          <span className="text-white/65">Patent Pending</span>
        </span>
      </div>
    </div>
  );
}
